import { Directive, ElementRef, HostListener, Input, Renderer2 } from '@angular/core';

/**
 * Zoom image directive.
 *
 * Note: Positioning and sizing of the zoomed image is done in the asset image component.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'img[zoomImage]',
  standalone: true,
})
export class ZoomImageDirective {
  @Input('zoomImage') set zoomContainer(zoomContainer: HTMLElement) {
    this._zoomContainer = zoomContainer;
    this.showZoomedImage(false);
  }
  get zoomContainer() {
    return this._zoomContainer;
  }
  private _zoomContainer!: HTMLElement;

  constructor(private readonly imgEl: ElementRef<HTMLImageElement>, private renderer: Renderer2) {}

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    const { x, y } = this._getPercentageOffset(event);
    this.updateZoomedImagePosition(x, y);
    this.showZoomedImage(true);
  }

  @HostListener('mouseout')
  onMouseOut() {
    this.showZoomedImage(false);
  }

  /**
   * Show/hide the zoomed image container by adding/removing the 'invisible' class
   *
   * @param show
   */
  private showZoomedImage(show: boolean) {
    if (!this._zoomContainer) {
      return;
    }

    if (show) {
      this.renderer.removeClass(this._zoomContainer, 'invisible');
    } else {
      this.renderer.addClass(this._zoomContainer, 'invisible');
    }
  }

  /**
   * Set the zoomed image position
   * @param offsetX
   * @param offsetY
   */
  private updateZoomedImagePosition(offsetX: number, offsetY: number) {
    if (!this._zoomContainer) {
      return;
    }

    this.renderer.setStyle(this._zoomContainer, 'background-position', `${offsetX}% ${offsetY}%`);
  }

  /**
   * Calculate the percentage offset of the mouse position in the image
   * @param event
   * @returns {x: number, y: number}
   * @private
   */
  private _getPercentageOffset(event: MouseEvent): { x: number; y: number } {
    const { offsetX, offsetY } = event;
    const { width, height } = this.imgEl.nativeElement;
    return {
      x: Math.round((offsetX / width) * 100),
      y: Math.round((offsetY / height) * 100),
    };
  }
}
